import { Box, Button, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { TTableStructure, TTableStructureFilter } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { getColumnFilterValue, getSeparateFilterValue } from 'utils/FilterParams';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import { ButtonCircularProgress } from 'ui/ButtonCircularProgress';
import { PAGE_SIZE } from 'services/constants/env';
import {
	getDailyExchangeRateListRequest,
	refreshDailyExchangeRatesRequest,
} from 'redux/reducers/exchangeRates/reducer';
import {
	getDailyExchangeRateList,
	getDailyExchangeRateListLoading,
	getDailyExchangeRateRefreshLoading,
} from 'redux/reducers/exchangeRates/selectors';
import { TDailyExchangeRate } from 'redux/reducers/exchangeRates/types';
import { getEmEnumsRequest } from 'redux/reducers/emManagement/reducer';
import { EEmEnums } from 'redux/reducers/emManagement/constants';
import {
	getEnumsCurrencyCode,
	getEnumsSupportedCurrency,
} from 'redux/reducers/emManagement/selectors';
import { tableStructure as defaultTableStructure } from './tableStructure';
import SyncIcon from '@mui/icons-material/Sync';
import { getUserIsAdmin } from '../../../../redux/reducers/user/selectors';
import { EExchangeRatesTabGroupLabel } from '../../constants';

/** The column whose separate filter value is sent to the API as its `currency` request field. */
const CURRENCY_FILTER_SELECTOR = 'fromCurrency';

export const DailyExchangeRates: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);
	const [refreshConfirmOpen, setRefreshConfirmOpen] = useState(false);

	const list = useSelector(getDailyExchangeRateList);
	const listLoading = useSelector(getDailyExchangeRateListLoading);
	const refreshLoading = useSelector(getDailyExchangeRateRefreshLoading);
	const currencyCode = useSelector(getEnumsCurrencyCode);
	const userIsAdmin = useSelector(getUserIsAdmin);
	useEffect(() => {
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.CURRENCY_CODE }));
	}, [dispatch]);

	const params = useMemo(() => queryString.parse(search), [search]);

	/** Reads the `f-fromCurrency` param, falling back to the default the column declares. */
	const currencyFilter = getColumnFilterValue(
		defaultTableStructure.find((column) => column.selector === CURRENCY_FILTER_SELECTOR),
		params,
	);

	/** The endpoint pages server side and filters by currency — it offers no sorting or search. */
	useEffect(() => {
		dispatch(
			getDailyExchangeRateListRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				currency: currencyFilter || null,
			}),
		);
	}, [currencyFilter, dispatch, params.page]);

	/** Currency codes the backend supports, offered as the options of the From Currency filter. */
	const currencyOptions = useMemo<TTableStructureFilter>(
		() =>
			(currencyCode ?? [])
				.filter((item) => item.value.length === 3)
				.map((item) => [item.value, item.displayName]),
		[currencyCode],
	);

	const structure = useMemo(
		() =>
			currentTableStructure.map((column) =>
				column.selector === CURRENCY_FILTER_SELECTOR
					? { ...column, filter: currencyOptions }
					: column,
			),
		[currencyOptions, currentTableStructure],
	);

	/** Keeps only the visibility flags, so the columns stay free of the derived filter options. */
	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TDailyExchangeRate>[]) => {
			setCurrentTableStructure((previous) =>
				previous.map((column) => {
					const changed = newTableStructure.find((item) => item.selector === column.selector);
					return changed ? { ...column, show: changed.show } : column;
				}),
			);
		},
		[],
	);

	const handleRefreshConfirm = useCallback(() => {
		setRefreshConfirmOpen(false);
		dispatch(refreshDailyExchangeRatesRequest());
	}, [dispatch]);

	return (
		<>
			<ConfirmationDialog
				open={refreshConfirmOpen}
				setClose={() => setRefreshConfirmOpen(false)}
				handleConfirm={handleRefreshConfirm}
				title="Sync Daily Exchange Rates"
				text="The rates will be fetched from the rate provider and stored. Do you want to continue?"
			/>
			<EasTableWithFilter<TDailyExchangeRate>
				tableStructure={structure}
				items={list}
				itemsIsLoading={listLoading}
				handleAction={() => undefined}
				onTableStructureChange={handleTableStructureChange}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary">
						Resources / Reference Tables / Exchange Rates /{' '}
						<b>{EExchangeRatesTabGroupLabel.DAILY_EXCHANGE_RATES}</b>
					</Typography>
					{userIsAdmin && (
						<Button
							variant="contained"
							color="primary"
							size="small"
							disabled={refreshLoading || listLoading}
							onClick={() => setRefreshConfirmOpen(true)}
							startIcon={refreshLoading ? <ButtonCircularProgress /> : <SyncIcon />}
						>
							Sync Daily Exchange Rates
						</Button>
					)}
				</Box>
			</EasTableWithFilter>
		</>
	);
};
